/**
 * 재추출 확인 창 구성 — 순수 함수(spec `2026-09-29-처방검토-보험코드-형제전파-재추출-단계옵션`).
 *
 * 확인 창의 판단(본문·[룰만] 활성·사유 문구)을 상세 페이지에서 떼어 내 여기서 고정한다.
 * 상세 페이지는 단위 테스트에서 마운트하지 않으므로(현황 7) 이 순수 함수가 컴포넌트 테스트 대상이다.
 *
 * 사유 문구는 **프런트 정본**이다(화면 설계 3). 6112 토스트도 `data.unavailableReason`을 이 매핑으로
 * 표시하고, 서버 `message`는 폴백이다.
 */
import { AdminDoseRuleDetail } from "services/api/doseRules/types";

/** 강등 경고(화면 설계 5). 되돌릴 수 없는 행동(4)의 고지. */
export const REEXTRACT_WARNING_TEXT = "승인된 룰은 본문이 바뀌면 검수 대기로 돌아갑니다.";

/** `룰만` 불가 사유 6종 → 본문/토스트 문구(프런트 정본, 화면 설계 3). */
export const REEXTRACT_REASON_TEXT: Record<string, string> = {
  missing: "Step 2 보관본이 없어 룰만 재추출할 수 없습니다. Step 2부터 재추출하세요.",
  step2_outdated: "Step 2 보관본 형식이 바뀌어 Step 2부터 재추출해야 합니다.",
  label_changed: "허가사항이 바뀌어 Step 2부터 재추출해야 합니다.",
  product_drift: "제품 정보가 Step 2 이후 바뀌어 Step 2부터 재추출해야 합니다.",
  step2_blocked: "Step 2 검산에서 막혀 룰만 재추출할 수 없습니다. Step 2부터 재추출하세요.",
  not_eligible: "용량 판정 대상이 아닌 품목이라 룰을 추출하지 않습니다.",
};

/** 알 수 없는 사유·null일 때(화면 설계 3). */
export const REEXTRACT_REASON_FALLBACK = "룰만 재추출을 할 수 없습니다.";

/** `step2Snapshot` 필드 자체가 없을 때(옛 서버, 구버전 공존 표). */
export const REEXTRACT_NO_SERVER_SUPPORT = "서버가 룰만 재추출을 지원하지 않습니다.";

/**
 * 사유 코드 → 문구. 6종은 매핑, 그 밖(미지·null)은 폴백.
 * **`REEXTRACT_NO_SERVER_SUPPORT`는 반환하지 않는다** — 그 케이스는 필드 부재 판정이라
 * `buildReextractConfirm`이 따로 다룬다(6112 토스트에는 오지 않는다).
 */
export const reextractReasonText = (reason?: string | null): string =>
  (reason && REEXTRACT_REASON_TEXT[reason]) || REEXTRACT_REASON_FALLBACK;

/** 확인 창 구성 모델 — 본문·경고·[룰만] 활성·사유 문구. */
export interface ReextractConfirmModel {
  /** 첫 줄(품목·범위·반영 시점). */
  bodyText: string;
  /** 둘째 줄(강등 경고). */
  warningText: string;
  /** [룰만 재추출] 활성 여부. */
  ruleOnlyEnabled: boolean;
  /** 불가 시 본문에 붙는 사유 한 줄(가능하면 null). */
  unavailableText: string | null;
}

/** 품목명은 `productRef.product_name`이다(dose_products엔 없음). 없으면 undefined. */
const productNameOf = (detail?: AdminDoseRuleDetail): string | undefined => {
  const name = detail?.productRef?.product_name;
  return typeof name === "string" && name.trim() ? name : undefined;
};

/**
 * 확인 창 구성(순수). `detail.step2Snapshot`으로 [룰만] 활성·사유를 정한다.
 * - 필드 부재(옛 서버) → disabled + `REEXTRACT_NO_SERVER_SUPPORT`
 * - `available === true` → 활성, 사유 없음
 * - `available === false` → disabled + `reextractReasonText(unavailableReason)`
 */
export const buildReextractConfirm = (detail?: AdminDoseRuleDetail): ReextractConfirmModel => {
  const name = productNameOf(detail);
  const subject = name ? `품목 ${name}` : "이 품목";
  const bodyText = `${subject}의 대표·전개 룰 전체를 다시 추출합니다. 반영은 잡 완료 후입니다.`;

  const snapshot = detail?.step2Snapshot;
  if (!snapshot) {
    return {
      bodyText,
      warningText: REEXTRACT_WARNING_TEXT,
      ruleOnlyEnabled: false,
      unavailableText: REEXTRACT_NO_SERVER_SUPPORT,
    };
  }
  if (snapshot.available) {
    return {
      bodyText,
      warningText: REEXTRACT_WARNING_TEXT,
      ruleOnlyEnabled: true,
      unavailableText: null,
    };
  }
  return {
    bodyText,
    warningText: REEXTRACT_WARNING_TEXT,
    ruleOnlyEnabled: false,
    unavailableText: reextractReasonText(snapshot.unavailableReason),
  };
};
